import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import {
  useComparisonMode,
  useEntities,
  useEntityNames,
  useGroupedMeasures,
  useSelectedCapacityTeams,
  useSelectedInitiativeCompletions,
  useSelectedInitiativeFocus,
  useSelectedInitiativeStatuses,
  useSelectedMeasureOverTime,
  useSelectedMeasures,
} from '../../../store/process-analysis-store/process-analysis-store.hooks';
import { useFlora } from '../../flora/flora.context';
import {
  formatCapacityForFlora,
  formatInitiativeChartForFlora,
  formatInitiativeOverviewForFlora,
  formatKeyMeasuresOverTimeForFlora,
  formatMeasureComparisonForFlora,
  formatMonthlyAveragesForFlora,
} from '../helpers/flora-data-formatter';
import { transformCapacityData } from '../main-section/capacity/table/table.helpers';
import { useChartData as useInitiativeCompletionChartData } from '../main-section/initiative-completion/line-chart/line-chart.hooks';
import { useChartData as useInitiativeFocusChartData } from '../main-section/initiative-focus/line-chart/line-chart.hooks';
import { useInitiativeOverviewTableData } from '../main-section/initiative-overview/table/table.hooks';
import { useChartData as useKeyMeasuresChartData } from '../main-section/key-measures-over-time/line-chart/line-chart.hooks';
import { useChartData as useMeasureComparisonChartData } from '../main-section/measure-comparison/line-chart/line-chart.hooks';
import { mergeMeasureMetaDataAndTargets } from '../main-section/measure-comparison/measure-comparison.helpers';
import { transformMonthlyAveragesData } from '../main-section/monthly-averages/table/table.helpers';
import {
  useFilteredMonthlyAveragesData,
  useSelectedEntities,
} from '../main-section/monthly-averages/table/table.hooks';
import {
  ComparisonMode,
  MetricType,
  MultipleViewMode,
  ProcessAnalysisFloraData,
  Tab,
  ViewMode,
} from '../process-analysis.type';
import { useCapacityData } from './capacity';
import { useKeyMeasuresOverTimeData } from './key-measures-over-time';
import { useMeasureComparisonData, useMeasureComparisonTargets } from './measure-comparison';
import { useMonthlyAveragesData } from './monthly-averages';

/**
 * Hook that provides Flora data for measure comparison chart
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {ProcessAnalysisFloraData | null} - Flora data for measure comparison chart
 */
const useMeasureComparisonFloraData = (enabled = true): ProcessAnalysisFloraData | null => {
  const { t } = useTranslation();
  const selectedMeasures = useSelectedMeasures();
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);
  const availableTargets = useMeasureComparisonTargets();
  const { data } = useMeasureComparisonData(enabled);
  const { contextualInfo } = useFlora();

  const mergedMeasureMetadata = useMemo(() => {
    return mergeMeasureMetaDataAndTargets(availableMeasures, availableTargets);
  }, [availableMeasures, availableTargets]);

  const chartData = useMeasureComparisonChartData(data, mergedMeasureMetadata);

  const floraData = useMemo(() => {
    if (!chartData.length || !selectedMeasures.length) return null;
    const selectedItems = selectedMeasures.map((measure) => ({ id: measure, name: measure }));
    return formatMeasureComparisonForFlora(chartData, selectedItems, mergedMeasureMetadata, contextualInfo, t);
  }, [chartData, selectedMeasures, mergedMeasureMetadata, contextualInfo, t]);

  if (!floraData) return null;

  return {
    data: [floraData],
    prompt: t('flora.process_analysis.measure_comparison_chart_prompt'),
    tooltip: t('flora.process_analysis.flora_button_tooltip'),
  };
};

/**
 * Hook that provides Flora data for key measures over time chart
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {ProcessAnalysisFloraData | null} - Flora data for key measures over time chart
 */
const useKeyMeasuresOverTimeFloraData = (enabled = true): ProcessAnalysisFloraData | null => {
  const { t } = useTranslation();
  const selectedMeasureOverTime = useSelectedMeasureOverTime();
  const entities = useEntities();
  const { entityNames } = useEntityNames();
  const { data } = useKeyMeasuresOverTimeData({ enabled });
  const { contextualInfo } = useFlora();

  const chartData = useKeyMeasuresChartData(data);

  const floraData = useMemo(() => {
    if (!chartData.length || !selectedMeasureOverTime || !entities.length) return null;
    const selectedItems = entities.map((entityId) => ({ id: entityId, name: entityId }));
    return formatKeyMeasuresOverTimeForFlora(
      chartData,
      selectedMeasureOverTime,
      entityNames,
      selectedItems,
      contextualInfo,
      t,
    );
  }, [chartData, selectedMeasureOverTime, entities, entityNames, contextualInfo, t]);

  if (!floraData) return null;

  return {
    data: [floraData],
    prompt: t('flora.process_analysis.key_measures_over_time_chart_prompt'),
    tooltip: t('flora.process_analysis.flora_button_tooltip'),
  };
};

/**
 * Hook that provides Flora data for initiative completion chart
 *
 * @returns {ProcessAnalysisFloraData | null} - Flora data for initiative completion chart
 */
const useInitiativeCompletionFloraData = (): ProcessAnalysisFloraData | null => {
  const i18n = useTranslation();
  const selectedInitiativeCompletions = useSelectedInitiativeCompletions();
  const chartData = useInitiativeCompletionChartData();
  const metric = useProcessAnalysisStore((state) => state.initiativeCompletionMetric);
  const { contextualInfo } = useFlora();

  const unitType = metric === MetricType.WorkUnits ? i18n.t('common.work_units') : i18n.t('common.estimation');

  const floraData = useMemo(() => {
    if (!chartData.length || !selectedInitiativeCompletions.length) return null;
    return formatInitiativeChartForFlora(
      chartData,
      selectedInitiativeCompletions,
      'completion',
      unitType,
      contextualInfo,
    );
  }, [chartData, selectedInitiativeCompletions, unitType, contextualInfo]);

  if (!floraData) return null;

  return {
    data: [floraData],
    prompt: i18n.t('flora.process_analysis.initiative_completion_chart_prompt'),
    tooltip: i18n.t('flora.process_analysis.flora_button_tooltip'),
  };
};

/**
 * Hook that provides Flora data for initiative focus chart
 *
 * @returns {ProcessAnalysisFloraData | null} - Flora data for initiative focus chart
 */
const useInitiativeFocusFloraData = (): ProcessAnalysisFloraData | null => {
  const i18n = useTranslation();
  const selectedInitiativeFocus = useSelectedInitiativeFocus();
  const chartData = useInitiativeFocusChartData();
  const metric = useProcessAnalysisStore((state) => state.initiativeFocusMetric);
  const { contextualInfo } = useFlora();

  const unitType = metric === MetricType.WorkUnits ? i18n.t('common.work_units') : i18n.t('common.estimation');

  const floraData = useMemo(() => {
    if (!chartData.length || !selectedInitiativeFocus.length) return null;
    return formatInitiativeChartForFlora(chartData, selectedInitiativeFocus, 'focus', unitType, contextualInfo);
  }, [chartData, selectedInitiativeFocus, unitType, contextualInfo]);

  if (!floraData) return null;

  return {
    data: [floraData],
    prompt: i18n.t('flora.process_analysis.initiative_focus_chart_prompt'),
    tooltip: i18n.t('flora.process_analysis.flora_button_tooltip'),
  };
};

/**
 * Hook that provides Flora data for capacity table
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {ProcessAnalysisFloraData | null} - Flora data for capacity table
 */
const useCapacityFloraData = (enabled = true): ProcessAnalysisFloraData | null => {
  const i18n = useTranslation();
  const selectedCapacityTeams = useSelectedCapacityTeams();
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const { portfolioData, projectData } = useCapacityData(enabled);
  const { contextualInfo } = useFlora();

  const floraData = useMemo(() => {
    if (!projectData || Object.keys(projectData).length === 0) return null;

    const selection = activeTab === Tab.Portfolios ? selectedCapacityTeams : selectedTeams;
    const projectIdToName = portfolio?.projects.map((project) => ({ id: project.id, name: project.name }));
    const tableData = transformCapacityData(projectData, portfolioData, selection, projectIdToName);

    if (!tableData.length) return null;
    return formatCapacityForFlora(tableData, contextualInfo);
  }, [projectData, portfolioData, activeTab, selectedCapacityTeams, selectedTeams, portfolio, contextualInfo]);

  if (!floraData) return null;

  return {
    data: [floraData],
    prompt: i18n.t('flora.process_analysis.capacity_table_prompt'),
    tooltip: i18n.t('flora.process_analysis.flora_button_tooltip'),
  };
};

/**
 * Hook that provides Flora data for initiative overview table
 *
 * @returns {ProcessAnalysisFloraData | null} - Flora data for initiative overview table
 */
const useInitiativeOverviewFloraData = (): ProcessAnalysisFloraData | null => {
  const i18n = useTranslation();
  const initiatives = useProcessAnalysisStore((state) => state.initiatives);
  const selectedInitiativeStatuses = useSelectedInitiativeStatuses();
  const { contextualInfo } = useFlora();

  const tableData = useInitiativeOverviewTableData(initiatives, selectedInitiativeStatuses);

  const floraData = useMemo(() => {
    if (!tableData.length) return null;
    return formatInitiativeOverviewForFlora(tableData, contextualInfo);
  }, [tableData, contextualInfo]);

  if (!floraData) return null;

  return {
    data: [floraData],
    prompt: i18n.t('flora.process_analysis.initiative_overview_table_prompt'),
    tooltip: i18n.t('flora.process_analysis.flora_button_tooltip'),
  };
};

/**
 * Hook that provides Flora data for monthly averages table
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {ProcessAnalysisFloraData | null} - Flora data for monthly averages table
 */
const useMonthlyAveragesFloraData = (enabled = true): ProcessAnalysisFloraData | null => {
  const i18n = useTranslation();
  const groupedMeasures = useGroupedMeasures();
  const { entityIds, entityNames } = useEntityNames();
  const { selectedEntities } = useSelectedEntities();
  const { data } = useMonthlyAveragesData(enabled);
  const { contextualInfo } = useFlora();

  const transformedData = useMemo(() => {
    return transformMonthlyAveragesData(data, entityIds, groupedMeasures);
  }, [data, entityIds, groupedMeasures]);

  const filteredData = useFilteredMonthlyAveragesData(transformedData);

  const floraData = useMemo(() => {
    if (Object.keys(filteredData).length === 0 || selectedEntities.length === 0) return null;
    return formatMonthlyAveragesForFlora(filteredData, selectedEntities, entityNames, contextualInfo);
  }, [filteredData, selectedEntities, entityNames, contextualInfo]);

  if (!floraData) return null;

  return {
    data: [floraData],
    prompt: i18n.t('flora.process_analysis.monthly_averages_table_prompt'),
    tooltip: i18n.t('flora.process_analysis.flora_button_tooltip'),
  };
};

/**
 * Hook that determines and returns Flora analysis data for process analysis views.
 *
 * This hook abstracts the logic for selecting the appropriate Flora data output
 * based on the user's current view and comparison mode state within process analysis.
 * It internally combines results from multiple specialized Flora data hooks
 * (e.g., measure comparison, key measures, initiative completion, initiative focus, capacity, etc.)
 * and selects which is relevant based on the user's selected modes and active tab.
 *
 * Key functionality:
 * - Checks current comparison mode (`Single` or `Multiple`) and view mode or multiple view mode.
 * - Selects and returns the corresponding prepared Flora data object, which contains
 *   the transformed table data, as well as the i18n-generated prompt and tooltip string
 *   suitable for Flora's LLM-based process analysis.
 * - Returns `null` if Flora data is not available or not relevant for the current context.
 * - PERFORMANCE: Only fetches data for the currently active view mode to reduce API calls.
 *
 * The returned object (or null) allows downstream components to trigger the Flora UI
 * for the current analytics view, providing context-sensitive, localized prompts and tooltips.
 *
 * @returns {ProcessAnalysisFloraData | null} - A data object containing:
 *    - `data`: The formatted data for Flora analysis (array, typically with one entry)
 *    - `prompt`: A localized prompt string for presenting to Flora
 *    - `tooltip`: A localized tooltip string for the Flora button
 *   Returns `null` if no data is eligible for the current UI view.
 */
const useFloraData = (): ProcessAnalysisFloraData | null => {
  const comparisonMode = useComparisonMode();
  const viewMode = useProcessAnalysisStore((state) => state.viewMode);
  const multipleViewMode = useProcessAnalysisStore((state) => state.multipleViewMode);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);

  // Calculate which hooks should be enabled based on current view
  // This reduces unnecessary API calls by only fetching data for the active view
  const isSingleMode = comparisonMode === ComparisonMode.Single;
  const isMultipleMode = comparisonMode === ComparisonMode.Multiple;

  const enableMeasureComparison = isSingleMode && viewMode === ViewMode.MeasureComparison;
  const enableKeyMeasures = isMultipleMode && multipleViewMode === MultipleViewMode.KeyMeasuresOverTime;
  const enableMonthlyAverages = isMultipleMode && multipleViewMode === MultipleViewMode.MonthlyAverages;
  const enableCapacity =
    (isSingleMode && viewMode === ViewMode.Capacity && activeTab === Tab.Portfolios) ||
    (isMultipleMode && multipleViewMode === MultipleViewMode.Capacity && activeTab === Tab.Teams);

  // Get Flora data hooks results - only enabled hooks will fetch data
  const measureComparisonData = useMeasureComparisonFloraData(enableMeasureComparison);
  const keyMeasuresData = useKeyMeasuresOverTimeFloraData(enableKeyMeasures);
  const monthlyAveragesData = useMonthlyAveragesFloraData(enableMonthlyAverages);
  const capacityData = useCapacityFloraData(enableCapacity);

  // Initiative hooks don't have expensive data-fetching, they use data already in store
  // so we don't need to conditionally disable them
  const initiativeCompletionData = useInitiativeCompletionFloraData();
  const initiativeFocusData = useInitiativeFocusFloraData();
  const initiativeOverviewData = useInitiativeOverviewFloraData();

  // Return the appropriate data based on current view
  if (isSingleMode) {
    switch (viewMode) {
      case ViewMode.MeasureComparison:
        return measureComparisonData;
      case ViewMode.InitiativeCompletion:
        return initiativeCompletionData;
      case ViewMode.InitiativeFocus:
        return initiativeFocusData;
      case ViewMode.Capacity:
        if (activeTab === Tab.Portfolios) {
          return capacityData;
        }
        return null;
      case ViewMode.InitiativeOverview:
        return initiativeOverviewData;
      default:
        return null;
    }
  }

  if (isMultipleMode) {
    switch (multipleViewMode) {
      case MultipleViewMode.KeyMeasuresOverTime:
        return keyMeasuresData;
      case MultipleViewMode.MonthlyAverages:
        return monthlyAveragesData;
      case MultipleViewMode.Capacity:
        if (activeTab === Tab.Teams) {
          return capacityData;
        }
        return null;
      default:
        return null;
    }
  }

  return null;
};

export { useFloraData };
